import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { motion, useScroll, useTransform } from "framer-motion";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useCursor } from "@/context/CursorContext";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { ProjectCard } from "@/components/ProjectCard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EASE = [0.22, 1, 0.36, 1];

const vimeoEmbed = (id) =>
  `https://player.vimeo.com/video/${id}?title=0&byline=0&portrait=0&color=5c6cf0`;

const CaseStudyPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const autoplayRequested = new URLSearchParams(location.search).get("autoplay") === "1";
  const { setCursor, reset } = useCursor();
  const [all, setAll] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPlayer, setShowPlayer] = useState(autoplayRequested);

  useEffect(() => {
    setLoading(true);
    setShowPlayer(autoplayRequested);
    document.documentElement.scrollTo({ top: 0, behavior: "auto" });
    axios
      .get(`${API}/projects`)
      .then((res) => setAll(res.data))
      .catch((e) => console.error("projects fetch failed", e))
      .finally(() => setLoading(false));
  }, [slug]);

  const project = useMemo(() => all.find((p) => p.slug === slug), [all, slug]);

  const siblings = useMemo(() => {
    if (!project) return { related: [], prev: null, next: null };
    const related = project.client
      ? all.filter((p) => p.slug !== project.slug && p.client === project.client).slice(0, 6)
      : [];
    const idx = all.findIndex((p) => p.slug === project.slug);
    return {
      related,
      prev: idx > 0 ? all[idx - 1] : null,
      next: idx >= 0 && idx < all.length - 1 ? all[idx + 1] : null,
    };
  }, [all, project]);

  // Scroll-linked cover parallax
  const { scrollY } = useScroll();
  const coverY = useTransform(scrollY, [0, 800], [0, 200]);
  const coverScale = useTransform(scrollY, [0, 800], [1, 1.12]);
  const coverOpacity = useTransform(scrollY, [0, 500, 900], [1, 0.7, 0.35]);

  if (loading) {
    return (
      <div className="relative bg-void min-h-screen">
        <Navbar onNavigate={(k) => navigate(k === "work" ? "/work" : `/#${k}`)} />
        <p className="pt-40 pb-40 font-mono text-sm text-bone/40 tracking-widest uppercase text-center">
          Loading…
        </p>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="relative bg-void min-h-screen">
        <Navbar onNavigate={(k) => navigate(k === "work" ? "/work" : `/#${k}`)} />
        <div className="pt-40 pb-40 text-center">
          <p className="font-mono text-sm text-bone/40 tracking-widest uppercase">
            Case study not found.
          </p>
          <Link
            to="/work"
            className="mt-8 inline-block font-mono text-[11px] tracking-[0.3em] uppercase text-bone underline"
          >
            Back to all work
          </Link>
        </div>
      </div>
    );
  }

  const clientSlug = project.client
    ? project.client.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")
    : null;

  return (
    <div className="relative bg-void min-h-screen">
      <Navbar onNavigate={(k) => navigate(k === "work" ? "/work" : `/#${k}`)} />

      {/* ---------- Full-bleed cinematic cover ---------- */}
      <section
        data-testid="case-hero"
        className="relative h-[100svh] w-full overflow-hidden"
      >
        <motion.div
          style={{ y: coverY, scale: coverScale, opacity: coverOpacity }}
          className="absolute inset-0 will-change-transform"
        >
          {project.poster && (
            <img
              src={project.poster}
              alt=""
              aria-hidden
              className="absolute inset-0 h-full w-full object-cover grayscale"
            />
          )}
          <div className="absolute inset-0 bg-gradient-to-b from-void/40 via-void/55 to-void" />
          <div className="absolute inset-0 bg-gradient-to-r from-void/60 via-transparent to-void/40" />
        </motion.div>

        <div className="relative z-10 flex h-full w-full max-w-[1600px] mx-auto flex-col justify-end px-6 md:px-12 pb-24 md:pb-32">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.1, ease: EASE }}
            className="max-w-5xl"
          >
            <div className="flex items-center gap-4 font-mono text-[11px] tracking-[0.35em] uppercase text-bone/60">
              {project.client ? (
                clientSlug ? (
                  <Link to={`/work/${clientSlug}`} className="hover:text-bone transition-colors">
                    {project.client}
                  </Link>
                ) : (
                  <span>{project.client}</span>
                )
              ) : (
                <span>Selected work</span>
              )}
              {project.year && (
                <>
                  <span className="opacity-40">/</span>
                  <span>{project.year}</span>
                </>
              )}
            </div>
            <h1
              data-testid="case-title"
              className="font-work mt-6 text-5xl sm:text-7xl md:text-8xl lg:text-9xl font-bold uppercase leading-[0.85] text-bone break-words"
            >
              {project.title}
            </h1>
          </motion.div>
        </div>

        {/* Scroll cue */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.6 }}
          transition={{ delay: 1.4, duration: 0.6 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 font-mono text-[10px] tracking-[0.4em] uppercase text-bone/50"
        >
          <span>Scroll</span>
          <span className="h-8 w-px bg-bone/40 origin-top animate-[grow_1.6s_ease-in-out_infinite]" />
        </motion.div>
      </section>

      {/* ---------- Player ---------- */}
      <section className="relative mx-auto max-w-[1600px] px-6 md:px-12 -mt-10 md:-mt-24">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-120px" }}
          transition={{ duration: 1, ease: EASE }}
          className="relative aspect-video w-full overflow-hidden bg-black shadow-[0_40px_80px_-30px_rgba(0,0,0,0.9)] border border-border"
          data-testid="case-player-wrap"
        >
          {showPlayer ? (
            <iframe
              key={project.slug}
              src={`${vimeoEmbed(project.embed_id)}&autoplay=1`}
              title={project.title}
              className="absolute inset-0 h-full w-full"
              allow="autoplay; fullscreen; picture-in-picture"
              allowFullScreen
              data-testid="case-iframe"
            />
          ) : (
            <button
              onClick={() => setShowPlayer(true)}
              onMouseEnter={() => setCursor("play")}
              onMouseLeave={reset}
              className="group absolute inset-0 flex h-full w-full items-center justify-center"
              aria-label="Play film"
            >
              {project.poster && (
                <img
                  src={project.poster}
                  alt=""
                  aria-hidden
                  className="absolute inset-0 h-full w-full object-cover"
                />
              )}
              <div className="absolute inset-0 bg-void/30 group-hover:bg-void/40 transition-colors duration-300" />
              <span className="relative flex h-24 w-24 md:h-32 md:w-32 items-center justify-center rounded-full bg-white/95 text-navy-ink shadow-2xl transition-transform duration-500 group-hover:scale-110">
                <span className="border-y-[14px] border-l-[22px] border-y-transparent border-l-navy-ink translate-x-1" />
              </span>
            </button>
          )}
        </motion.div>
      </section>

      {/* ---------- Brief + description ---------- */}
      <section className="mx-auto max-w-[1600px] px-6 md:px-12 mt-24 md:mt-36">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-y-14 lg:gap-x-16">
          <aside className="lg:col-span-4 space-y-8" data-testid="case-brief">
            {project.client && (
              <div>
                <p className="font-mono text-[10px] tracking-[0.4em] uppercase text-bone/40">
                  Client
                </p>
                <p className="mt-2 font-display text-2xl font-medium uppercase text-bone">
                  {project.client}
                </p>
              </div>
            )}
            {project.year && (
              <div>
                <p className="font-mono text-[10px] tracking-[0.4em] uppercase text-bone/40">
                  Year
                </p>
                <p className="mt-2 font-display text-2xl font-medium uppercase text-bone">
                  {project.year}
                </p>
              </div>
            )}
            <div>
              <p className="font-mono text-[10px] tracking-[0.4em] uppercase text-bone/40">
                Discipline
              </p>
              <p className="mt-2 font-display text-2xl font-medium uppercase text-bone">
                Motion · Video
              </p>
            </div>
          </aside>

          <div className="lg:col-span-8 lg:col-start-6">
            {project.desc_en ? (
              <p className="font-body font-light text-xl md:text-2xl text-bone/80 leading-relaxed whitespace-pre-wrap">
                {project.desc_en}
              </p>
            ) : (
              <p className="font-body font-light text-lg text-bone/40 italic">
                No brief was written on Vimeo for this piece. Watch the film above — it speaks for itself.
              </p>
            )}
          </div>
        </div>
      </section>

      {/* ---------- More from this client ---------- */}
      {siblings.related.length > 0 && (
        <section className="mx-auto max-w-[1600px] px-6 md:px-12 mt-32 md:mt-48">
          <div className="mb-14 flex items-end justify-between gap-6">
            <div>
              <p className="font-mono text-[11px] tracking-[0.4em] uppercase text-bone/40">
                More from
              </p>
              <h2 className="font-work mt-3 text-4xl md:text-6xl font-bold uppercase leading-[0.9] text-bone break-words">
                {project.client}
              </h2>
            </div>
            {clientSlug && (
              <Link
                to={`/work/${clientSlug}`}
                onMouseEnter={() => setCursor("link")}
                onMouseLeave={reset}
                className="group hidden md:inline-flex items-center gap-3 font-mono text-[11px] tracking-[0.3em] uppercase text-bone/60 hover:text-bone transition-colors"
              >
                <span>See all</span>
                <span className="transition-transform duration-300 group-hover:translate-x-1">→</span>
              </Link>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-x-8 gap-y-16">
            {siblings.related.map((p, i) => (
              <ProjectCard key={p.slug} project={p} index={i} onOpen={() => navigate(`/case/${p.slug}`)} />
            ))}
          </div>
        </section>
      )}

      {/* ---------- Prev / next ---------- */}
      <section className="mx-auto max-w-[1600px] px-6 md:px-12 mt-32 md:mt-48 mb-24">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border-t border-border pt-10">
          <div>
            {siblings.prev && (
              <Link
                to={`/case/${siblings.prev.slug}`}
                onMouseEnter={() => setCursor("link")}
                onMouseLeave={reset}
                data-testid="case-prev"
                className="group inline-flex flex-col gap-2"
              >
                <span className="font-mono text-[10px] tracking-[0.4em] uppercase text-bone/40">
                  ← Previous
                </span>
                <span className="font-work text-2xl md:text-3xl font-medium uppercase text-bone group-hover:text-navy-ink transition-colors">
                  {siblings.prev.title}
                </span>
              </Link>
            )}
          </div>
          <div className="md:text-right">
            {siblings.next && (
              <Link
                to={`/case/${siblings.next.slug}`}
                onMouseEnter={() => setCursor("link")}
                onMouseLeave={reset}
                data-testid="case-next"
                className="group inline-flex flex-col gap-2 md:items-end"
              >
                <span className="font-mono text-[10px] tracking-[0.4em] uppercase text-bone/40">
                  Next →
                </span>
                <span className="font-work text-2xl md:text-3xl font-medium uppercase text-bone group-hover:text-navy-ink transition-colors">
                  {siblings.next.title}
                </span>
              </Link>
            )}
          </div>
        </div>
      </section>

      <Footer onNavigate={(k) => navigate(`/#${k}`)} />

      <style>{`
        @keyframes grow {
          0%, 100% { transform: scaleY(0.2); opacity: 0.3; }
          50% { transform: scaleY(1); opacity: 1; }
        }
      `}</style>
    </div>
  );
};

export default CaseStudyPage;
